import * as React from "react";
import { AlertColor, FormControl, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { FeatureFlag } from "../../../../featureFlags";
import { useUpdateApplicationFeatureMutation } from "../../../../services/cclTokenedApplicationFeaturesApi";
import { UpdateApplicationFeature } from "../../../../services/types/applicationFeatureTypes";
import {
    eRoleIds,
    UserClaimsService,
} from "../../../../services/currentUserService/currentUserService";
import ApplicationFeaturesDataGridPro from "./admininistrationDataGrid";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventFeatureDetails } from "../../../../services/types/accessEventTypes";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import { SelectOption } from "../../../../components/common/selectOptions/selectOption";
import SystemAlert from "../../../home/systemAlert";

const Administration: React.FC = (props) => {
    const { logEvent } = useLogAccessEvent();
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");

    const features = React.useContext(FeatureFlag);
    const claimservice = new UserClaimsService();

    const [update, { isLoading }] = useUpdateApplicationFeatureMutation();

    const isUserFeatureManager = claimservice.IsUserInAppRole(eRoleIds.eFeatureManager);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const systemAlerts: SelectOption[] = [
        { val: "SystemAlert", option: "General System Alert" },
        { val: "ClientAdminSystemAlert", option: "Client Admin System Alert" },
    ];

    const [selectedAlertTag, setSelectedAlertTag] = React.useState<string>(systemAlerts[0].val);

    const handleAlertSelect = (event: SelectChangeEvent) => {
        setSelectedAlertTag(event.target.value);
    };

    const toggleFeature = (id: number) => {
        console.log(features);
        const updateFeature: UpdateApplicationFeature = {
            Id: id,
            UserEmail: claimservice.GetCurrentUserEmail(),
        };
        update(updateFeature)
            .unwrap()
            .then(() => {
                const fname =
                    features.allFeatures?.find((f: any) => f.applicationFeatureId === id)?.name ??
                    "Unknown";
                ShowSnackbarElement("Feature Updated", "success");
                const evtData: AccessEventFeatureDetails = {
                    featureId: id.toString(),
                    featureName: fname,
                };
                logEvent("ApplicationFeatureChanged", evtData);
            })
            .catch((error) => {
                ShowSnackbarElement("Feature Update Failed", "error");
            });
    };

    return (
        <Stack spacing={2} sx={{ width: "100%" }}>
            <CclGenericAccordian
                headingText="CCL Access Features"
                defaultExpanded={true}
                noGutter={true}
            >
                <ApplicationFeaturesDataGridPro
                    readonly={!isUserFeatureManager}
                    features={features.allFeatures}
                    triggerUpdateFlow={toggleFeature}
                    loading={isLoading}
                />
            </CclGenericAccordian>
            {isUserFeatureManager ? (
                <CclGenericAccordian
                    headingText="CCL Access System Alerts"
                    defaultExpanded={true}
                    noGutter={true}
                >
                    <Stack spacing={4} sx={{ width: "100%", p: 1 }}>
                        <FormControl sx={{ width: "50%" }}>
                            <Select
                                value={selectedAlertTag}
                                onChange={handleAlertSelect}
                                sx={{
                                    "& .MuiSelect-select": {
                                        paddingTop: 2,
                                        paddingBottom: 2,
                                        display: "flex",
                                        alignItems: "center",
                                    },
                                }}
                                size="small"
                            >
                                {systemAlerts.map((alert) => (
                                    <MenuItem
                                        key={alert.val}
                                        value={alert.val}
                                        sx={{ display: "flex", alignItems: "center" }}
                                    >
                                        {alert.option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <SystemAlert
                            tag={selectedAlertTag}
                            title={
                                systemAlerts.find((a) => a.val === selectedAlertTag)?.option ?? ""
                            }
                        />
                    </Stack>
                </CclGenericAccordian>
            ) : null}

            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
        </Stack>
    );
};

export default Administration;
